import { add, endOfMonth, isBefore, parseISO, startOfToday } from 'date-fns';
import isNil from 'lodash/isNil';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { DocumentStatus, DocumentType } from '~/types/document';
import { DocumentBuilderFlowForm } from '~/types/document-builder';
import {
  Flow,
  FlowPatchForm,
  FlowPaymentStatus,
  FlowPaymentTerms,
  FlowWithDocument,
  PaymentMethod,
  UpdateFlowValues,
} from '~/types/flow';
import { isDefined } from '~/types/typeGuards';
import { formatFromISOToDisplay } from '~/utils/formatDate';
import { isNotEmpty } from '~/utils/helpers';
import { negateMoney } from './money';
import { isPaymentCanceled, isPaymentInProgress, isPaymentSucceeded, isPaymentTimeout } from './payment';
import { sortObjectsAlphabetically } from './sort';

export const isPaymentOverdue = (flow: Flow): boolean => {
  return flow.paymentStatus !== FlowPaymentStatus.Paid && isBefore(parseISO(flow.expectedPaymentDate), startOfToday());
};

export function parseDocumentFlow(flow: Flow): DocumentBuilderFlowForm {
  return {
    remoteId: flow.id,
    amount: flow.amount.amount,
    expectedPaymentDate: parseISO(flow.expectedPaymentDate),
    notes: flow.notes ?? '',
    paymentStatus: flow.paymentStatus,
  };
}
export const flowAmount = (flow: FlowWithDocument) => (flow.document.isInflow ? flow.amount : negateMoney(flow.amount));

export const buildFlowUpdatePayload = (values: FlowPatchForm, changed: string[]): Partial<UpdateFlowValues> => {
  const payload: Partial<UpdateFlowValues> = {};

  if (changed.includes('totalAmount') || changed.includes('currency')) {
    payload.amount = { amount: values.totalAmount, currency: values.currency };
  }
  if (changed.includes('expectedPaymentDate')) {
    payload.expectedPaymentDate = values.expectedPaymentDate;
  }
  if (changed.includes('paymentMethod')) {
    payload.paymentMethod = values.paymentMethod;
  }
  if (changed.includes('paymentAccount')) {
    payload.paymentAccount = values.paymentAccount;
  }
  if (changed.includes('account')) {
    payload.account = values.account.id ? { id: values.account.id } : null;
  }
  if (changed.includes('notes')) {
    payload.notes = values.notes === '' ? null : values.notes;
  }
  if (changed.includes('paymentStatus')) {
    payload.paymentStatus = values.paymentStatus;
  }

  return payload;
};

const getPayments = (flow: Flow) => flow.payments ?? [];

export const getSortedPayments = (flow: Flow) => {
  return [...getPayments(flow)].sort(sortObjectsAlphabetically((e) => e.createdAt)).reverse();
};

export const getSucceededPayment = (flow: Flow) => getPayments(flow).find(isPaymentSucceeded);
export const getTimeoutPayment = (flow: Flow) => getPayments(flow).find(isPaymentTimeout);

export const blockPaymentStautsChange = (flow: Flow) => {
  const payment = getMostRelevantPayment(flow);

  if (isNil(payment)) return false;

  if (flow.paymentStatus === FlowPaymentStatus.ToPay && isPaymentInProgress(payment)) return true;
  if (flow.paymentStatus === FlowPaymentStatus.Paid && isPaymentSucceeded(payment)) return true;

  return false;
};

export const getMostRelevantPayment = (flow: Flow) => {
  const succeededPayment = getSucceededPayment(flow);
  if (succeededPayment) return succeededPayment;

  const inProgressPayment = getPayments(flow).find(isPaymentInProgress);
  if (inProgressPayment) return inProgressPayment;

  return getSortedPayments(flow).find((el) => !isPaymentCanceled(el));
};

export const flowCanBePayed = ({ flow }: { flow: Flow }) => {
  const hasSuccessPayment = getSucceededPayment(flow);
  const hasTimeoutPayment = getTimeoutPayment(flow);
  return (
    !hasSuccessPayment &&
    !hasTimeoutPayment &&
    flow.paymentStatus === FlowPaymentStatus.ToPay &&
    flow.document?.status !== DocumentStatus.Draft &&
    !flow.document?.isInflow &&
    flow.paymentMethod !== PaymentMethod.TaxForm
  );
};

export const flowCanBeIncludedInRemittance = (flow: FlowWithDocument) =>
  flow.document.isInflow &&
  flow.document.status !== DocumentStatus.Draft &&
  flow.paymentStatus !== FlowPaymentStatus.Paid &&
  flow.document.hasAttachment &&
  flow.document.documentType === DocumentType.Invoice &&
  flow.amount.currency === 'EUR';

export const flowCanBeReminded = (flow: FlowWithDocument) =>
  flow.document.isInflow &&
  flow.document.status !== DocumentStatus.Draft &&
  flow.paymentStatus !== FlowPaymentStatus.Paid;

export const isWaitingForPaymentResult = (flow: Flow) => {
  const payment = getPayments(flow).find(isPaymentInProgress);
  return isDefined(payment);
};

export const extractFlowNumber = (
  flow: FlowWithDocument,
): { index: undefined; count: undefined } | { count: number; index: undefined | number } => {
  const document = flow.document;

  const count = document.flows?.length ?? 0;
  if (count === 0) return { count: undefined, index: undefined };

  const index =
    document.flows
      ?.map((x) => [x.paymentDate, x.id])
      .sort() /// I am sorting because i am copying the data
      .findIndex(([, id]) => flow.id === id) ?? -1;

  return { index: index === -1 ? undefined : index + 1, count };
};

export const calculatePresetDate = (baseDate: Date, delay: FlowPaymentTerms | undefined | null): Date => {
  switch (delay) {
    case FlowPaymentTerms.Plus15:
      return add(baseDate, { days: 15 });
    case FlowPaymentTerms.Plus30:
      return add(baseDate, { days: 30 });
    case FlowPaymentTerms.Plus60:
      return add(baseDate, { days: 60 });
    case FlowPaymentTerms.Plus90:
      return add(baseDate, { days: 90 });
    case FlowPaymentTerms.Plus120:
      return add(baseDate, { days: 120 });
    case FlowPaymentTerms.Plus150:
      return add(baseDate, { days: 150 });
    case FlowPaymentTerms.EOM:
      return endOfMonth(baseDate);
    case FlowPaymentTerms.Plus30EOM:
      return endOfMonth(add(baseDate, { days: 30 }));
    case FlowPaymentTerms.Plus60EOM:
      return endOfMonth(add(baseDate, { days: 60 }));
    case FlowPaymentTerms.Plus90EOM:
      return endOfMonth(add(baseDate, { days: 90 }));
    case FlowPaymentTerms.Plus120EOM:
      return endOfMonth(add(baseDate, { days: 120 }));
    case FlowPaymentTerms.Plus150EOM:
      return endOfMonth(add(baseDate, { days: 150 }));
    case FlowPaymentTerms.EOMPlus15:
      return add(endOfMonth(baseDate), { days: 15 });
    case FlowPaymentTerms.Plus30EOMPlus15:
      return add(endOfMonth(add(baseDate, { days: 30 })), { days: 15 });
    case FlowPaymentTerms.Plus60EOMPlus15:
      return add(endOfMonth(add(baseDate, { days: 60 })), { days: 15 });
    case FlowPaymentTerms.Plus90EOMPlus15:
      return add(endOfMonth(add(baseDate, { days: 90 })), { days: 15 });
    case FlowPaymentTerms.Plus120EOMPlus15:
      return add(endOfMonth(add(baseDate, { days: 120 })), { days: 15 });
    default:
      return baseDate;
  }
};

export const useFlowPaymentReason = () => {
  const { t } = useTranslation('payments', {
    keyPrefix: 'make_a_flow_payment_dialog',
  });
  return useCallback(
    (mainFlow: FlowWithDocument, otherFlows: FlowWithDocument[]) => {
      if (isNotEmpty(otherFlows)) {
        return t('payment_reason_multi_flows', {
          invoicesNumber: [mainFlow]
            .concat(otherFlows)
            .map((flow) => flow.document.number ?? '')
            .join(', '),
        });
      }

      return t('payment_reason', {
        context: mainFlow.document.documentType,
        invoiceNumber: mainFlow.document.number ?? '',
      });
    },
    [t],
  );
};

const generateFlowCaption = (
  t: ReturnType<typeof useTranslation<'payments', 'make_a_flow_payment_dialog'>>['t'],
  flow: FlowWithDocument,
  otherFlows: FlowWithDocument[],
) => {
  if (isNil(flow.document.number)) {
    return t('flow_without_number_reference', {
      context: flow.document.documentType,
      invoiceCreationDate: formatFromISOToDisplay(flow.document.creationDate),
    });
  }

  if (isNotEmpty(otherFlows.length)) {
    return t('flow_with_number_multiple_flows_reference', {
      context: flow.document.documentType,
      invoiceNumber: flow.document.number,
    });
  }

  return t('flow_with_number_single_flow_reference', {
    context: flow.document.documentType,
    invoiceNumber: flow.document.number,
    invoiceCreationDate: formatFromISOToDisplay(flow.document.creationDate),
  });
};

export const useFlowHeaderCaption = () => {
  const { t } = useTranslation('payments', {
    keyPrefix: 'make_a_flow_payment_dialog',
  });

  return useCallback(
    (mainFlow: FlowWithDocument, otherFlows: FlowWithDocument[]) => {
      if (isNotEmpty(otherFlows)) {
        return [
          generateFlowCaption(t, mainFlow, otherFlows),
          ...[otherFlows.map((f) => generateFlowCaption(t, f, otherFlows))],
        ].join(', ');
      }

      return [
        generateFlowCaption(t, mainFlow, otherFlows),
        t('expire_on', { dueDate: formatFromISOToDisplay(mainFlow.expectedPaymentDate) }),
      ].join(' • ');
    },
    [t],
  );
};
